import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { USER_ACCOUNT_BUSINESS } from '../../../constants/userDefs';
import { ReactComponent as HokErrorIcon } from '../../../img/icons/hok-error.svg';
import { ReactComponent as HokSuccessIcon } from '../../../img/icons/hok-success.svg';
import { formatDateTime } from '../../../constants/dateFormatting';

const USER_ID_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 130,
  Header: <FormattedMessage id="adminUsersListPage.columns.id" />,
  accessor: 'partnerId',
};

const USER_EMAIL_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 240,
  Header: <FormattedMessage id="adminUsersListPage.columns.email" />,
  accessor: 'email',
};

const USER_PHONE_NUMBER_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 165,
  Header: <FormattedMessage id="adminUsersListPage.columns.phoneNumber" />,
  accessor: 'phoneNumber',
};

const USER_OIB_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 130,
  Header: <FormattedMessage id="adminUsersListPage.columns.OIB" />,
  accessor: 'oib',
};

const USER_FIRST_NAME_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 150,
  Header: <FormattedMessage id="adminUsersListPage.columns.firstName" />,
  accessor: 'firstName',
  id: 'opBusinessFirstNameOrFirstNameLike',
  Cell: rowInfo =>
    rowInfo.original.sector === USER_ACCOUNT_BUSINESS ? rowInfo.original.businessFirstName : rowInfo.original.firstName,
};

const USER_LAST_NAME_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 150,
  Header: <FormattedMessage id="adminUsersListPage.columns.lastName" />,
  accessor: 'lastName',
  id: 'opBusinessLastNameOrLastNameOr',
  Cell: rowInfo =>
    rowInfo.original.sector === USER_ACCOUNT_BUSINESS ? rowInfo.original.businessLastName : rowInfo.original.lastName,
};

const USER_SECTOR_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'sector',
  accessor: 'sector',
  Header: <FormattedMessage id="adminUsersListPage.columns.accountType" />,
  Cell: rowInfo => (
    <div>
      {rowInfo.original.sector && <FormattedMessage id={`adminUsersListPage.filter.${rowInfo.original.sector}`} />}
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => onChange(event.target.value)}
      value={filter ? filter.value : ''}
    >
      <option value="">{intl.formatMessage({ id: 'adminUsersListPage.filter.all' })}</option>
      <option value="private">{intl.formatMessage({ id: 'adminUsersListPage.filter.private' })}</option>
      <option value="business">{intl.formatMessage({ id: 'adminUsersListPage.filter.business' })}</option>
    </select>
  )),
};

const USER_ACCOUNT_STATUS_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'verified',
  accessor: 'verified',
  Header: <FormattedMessage id="adminUsersListPage.columns.accountStatus" />,
  Cell: rowInfo => (
    <div className="d-flex align-items-center justify-content-center h-100">
      {rowInfo.original.verified ? (
        <HokSuccessIcon className="svg-table-icons svg-green" />
      ) : (
        <HokErrorIcon className="svg-table-icons svg-red" />
      )}
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => {
        const value = event.target.value ? event.target.value === 'true' : '';
        onChange(value);
      }}
      value={filter ? filter.value : ''}
    >
      <option value="">{intl.formatMessage({ id: 'adminUsersListPage.filter.all' })}</option>
      <option value="true">{intl.formatMessage({ id: 'adminUsersListPage.filter.verified' })}</option>
      <option value="false">{intl.formatMessage({ id: 'adminUsersListPage.filter.notVerified' })}</option>
    </select>
  )),
};

const USER_DELETED_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'deleted',
  accessor: 'deleted',
  Header: <FormattedMessage id="adminUsersListPage.columns.deleted" />,
  Cell: rowInfo => (
    <div className="d-flex align-items-center justify-content-center h-100">
      {rowInfo.original.deleted ? (
        <HokErrorIcon className="svg-table-icons svg-gray" />
      ) : (
        <HokSuccessIcon className="svg-table-icons svg-green" />
      )}
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => {
        let value = !event.target.value ? false : event.target.value;

        switch (value) {
          case 'true':
            onChange(true);
            break;

          default:
            onChange(false);
            break;
        }
      }}
      value={filter ? filter.value : ''}
    >
      {/* <option value="">{intl.formatMessage({ id: 'adminUsersListPage.filter.all' })}</option> */}
      <option value={false}>{intl.formatMessage({ id: 'adminUsersListPage.filter.notDeleted' })}</option>
      <option value={true}>{intl.formatMessage({ id: 'adminUsersListPage.filter.deleted' })}</option>
    </select>
  )),
};

const USER_CREATED_AT_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 130,
  Header: <FormattedMessage id="adminUsersListPage.columns.createdAt" />,
  accessor: 'createdAt',
  Cell: rowInfo => <div>{formatDateTime(rowInfo.original.createdAt, false)}</div>,
  Filter: <div />,
};

export const columns = [
  USER_EMAIL_COLUMN,
  USER_PHONE_NUMBER_COLUMN,
  USER_OIB_COLUMN,
  USER_ID_COLUMN,
  USER_FIRST_NAME_COLUMN,
  USER_LAST_NAME_COLUMN,
  USER_SECTOR_COLUMN,
  USER_ACCOUNT_STATUS_COLUMN,
  USER_DELETED_COLUMN,
  USER_CREATED_AT_COLUMN,
];
